import React, { useState } from 'react';
import {
  AudioLines,
  Search,
  Square,
  Volume2,
  Repeat,
  Zap,
  X,
  ChevronRight,
  ChevronLeft,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Slider } from '../ui/slider';
import SoundCard from './SoundCard';

// ─────────────────────────────────────────────────────────────────────────────
// Default cover placeholder
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_COVER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Crect width='80' height='80' rx='6' fill='%231e293b'/%3E%3Ccircle cx='40' cy='36' r='14' fill='none' stroke='%234b5563' stroke-width='2'/%3E%3Cpath d='M33 50 Q40 58 47 50' fill='none' stroke='%234b5563' stroke-width='2'/%3E%3C/svg%3E";

// ─────────────────────────────────────────────────────────────────────────────
// CategoryCard — compact card for selecting a SoundPad category
// ─────────────────────────────────────────────────────────────────────────────
const CategoryCard = ({ category, isSelected, activeCounts, onClick, lang = 'fr' }) => {
  const [imgErr, setImgErr] = useState(false);
  const coverSrc = !imgErr && category.cover ? category.cover : DEFAULT_COVER;
  const activeCount = activeCounts[category.id] || 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-lg border p-2 text-left transition-all w-full',
        'hover:bg-muted/50 hover:border-primary/30',
        isSelected
          ? 'border-primary/50 bg-primary/5 shadow-sm'
          : 'border-border/50',
      )}
    >
      <img
        src={coverSrc}
        alt={category.name}
        width={40}
        height={40}
        onError={() => setImgErr(true)}
        className="rounded-md object-cover shrink-0 border border-border/40 bg-muted"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{category.displayName || category.name}</p>
        <p className="text-[10px] text-muted-foreground">
          {category.soundCount} {lang === 'fr' ? 'sons' : 'sounds'}
        </p>
      </div>
      {activeCount > 0 && (
        <Badge className="shrink-0 text-[10px] px-1.5 py-0 h-4 bg-primary">
          {activeCount}
        </Badge>
      )}
      <ChevronRight
        className={cn(
          'h-4 w-4 shrink-0 transition-colors',
          isSelected ? 'text-primary' : 'text-muted-foreground/40',
        )}
      />
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ActiveSoundsMixer — header mixer showing all currently active sounds
// ─────────────────────────────────────────────────────────────────────────────
const ActiveSoundsMixer = ({
  activeSounds,
  soundVolumes,
  defaultSoundVolume,
  masterVolume,
  onStopSound,
  onStopAll,
  onSetSoundVolume,
  onSetMasterVolume,
  lang = 'fr',
}) => {
  const [expanded, setExpanded] = useState(true);

  if (activeSounds.length === 0) return null;

  return (
    <div className="flex-shrink-0 border-b border-border/60 bg-muted/10 px-4 py-2.5 space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-2 text-xs font-semibold hover:text-primary transition-colors"
          onClick={() => setExpanded((p) => !p)}
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
          {activeSounds.length} {lang === 'fr' ? `son${activeSounds.length > 1 ? 's' : ''} actif${activeSounds.length > 1 ? 's' : ''}` : `active sound${activeSounds.length > 1 ? 's' : ''}`}
        </button>
        <div className="flex items-center gap-1">
          {/* Master volume */}
          <Volume2 className="h-3 w-3 text-muted-foreground" />
          <Slider
            value={[masterVolume * 100]}
            onValueChange={([val]) => onSetMasterVolume(val / 100)}
            max={100}
            step={1}
            className="w-16"
            aria-label="Master volume"
          />
          <span className="text-[10px] text-muted-foreground tabular-nums w-7 text-right">
            {Math.round(masterVolume * 100)}%
          </span>
          <Button
            variant="destructive"
            size="sm"
            className="h-6 text-[10px] px-2 ml-1"
            onClick={onStopAll}
          >
            <Square className="h-2.5 w-2.5 mr-0.5" />
            Stop all
          </Button>
        </div>
      </div>

      {/* Active sounds list */}
      {expanded && (
        <div className="space-y-1">
          {activeSounds.map((sound) => {
            const vol = soundVolumes[sound.id] ?? defaultSoundVolume;
            return (
              <div
                key={sound.id}
                className="flex items-center gap-2 rounded bg-background/60 px-2 py-1"
              >
                <span className="text-[10px] font-medium truncate flex-1 min-w-0">
                  {sound.displayName || sound.name}
                </span>
                {sound.isLoop && (
                  <Repeat className="h-2.5 w-2.5 text-primary shrink-0" />
                )}
                <Slider
                  value={[vol * 100]}
                  onValueChange={([val]) =>
                    onSetSoundVolume(sound.id, val / 100)
                  }
                  max={100}
                  step={1}
                  className="w-16"
                  aria-label={`Volume ${sound.name}`}
                />
                <span className="text-[9px] text-muted-foreground tabular-nums w-5 text-right">
                  {Math.round(vol * 100)}
                </span>
                <button
                  type="button"
                  className="h-5 w-5 flex items-center justify-center rounded hover:bg-destructive/20 hover:text-destructive transition-colors text-muted-foreground shrink-0"
                  onClick={() => onStopSound(sound.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SoundboardLibrary — full-screen Sheet for browsing all sound categories
// ─────────────────────────────────────────────────────────────────────────────
const SoundboardLibrary = ({
  open,
  onOpenChange,
  categories,
  selectedCategoryId,
  onSelectCategory,
  filteredSounds,
  activeSoundIds,
  activeSounds,
  soundVolumes,
  masterVolume,
  defaultSoundVolume,
  searchTerm,
  onSearchChange,
  onToggleSound,
  onStopSound,
  onStopAll,
  onSetSoundVolume,
  onSetMasterVolume,
  isMobile,
  lang = 'fr',
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Count active sounds per category
  const activeCounts = {};
  for (const sound of activeSounds) {
    const catId = sound.categoryId;
    if (catId) {
      activeCounts[catId] = (activeCounts[catId] || 0) + 1;
    }
  }

  // Filter display
  const showAll = !selectedCategoryId;
  const loopSounds = filteredSounds.filter((s) => s.isLoop);
  const oneshotSounds = filteredSounds.filter((s) => !s.isLoop);

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-[48] bg-black/50 backdrop-blur-[1px] transition-opacity"
          aria-hidden="true"
          onClick={() => onOpenChange(false)}
        />
      )}

      <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
        <SheetContent
          side={isMobile ? 'bottom' : 'right'}
          hideOverlay
          className={cn(
            'w-full sm:max-w-3xl p-0 gap-0',
            isMobile && 'h-[90vh] rounded-t-xl border-t',
          )}
        >
          <div className="flex h-full flex-col overflow-hidden">
            {/* Header */}
            <SheetHeader className="flex-shrink-0 px-4 py-3 border-b border-border/60">
              <SheetTitle className="flex items-center gap-2">
                <AudioLines className="h-5 w-5" />
                Soundboard
              </SheetTitle>
            </SheetHeader>

            {/* Active sounds mixer */}
            <ActiveSoundsMixer
              activeSounds={activeSounds}
              soundVolumes={soundVolumes}
              defaultSoundVolume={defaultSoundVolume}
              masterVolume={masterVolume}
              onStopSound={onStopSound}
              onStopAll={onStopAll}
              onSetSoundVolume={onSetSoundVolume}
              onSetMasterVolume={onSetMasterVolume}
              lang={lang}
            />

            {/* Search bar */}
            <div className="flex-shrink-0 px-4 py-2.5 border-b border-border/60">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={lang === 'fr' ? 'Rechercher un son...' : 'Search for a sound...'}
                  className="pl-8 h-9"
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                    onClick={() => onSearchChange('')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Main content: sidebar + sounds grid */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left sidebar: categories (collapsible) */}
              {sidebarOpen && (
                <div className="w-52 shrink-0 border-r border-border/60 flex flex-col">
                  <div className="px-3 py-2 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onSelectCategory(null)}
                      className={cn(
                        'flex-1 text-left text-xs font-medium px-2 py-1.5 rounded transition-colors',
                        !selectedCategoryId
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                      )}
                    >
                      {lang === 'fr' ? 'Tous les sons' : 'All sounds'} ({categories.reduce((s, c) => s + c.soundCount, 0)})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                      className="shrink-0 h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title={lang === 'fr' ? 'Masquer les bibliothèques' : 'Hide libraries'}
                    >
                      <PanelLeftClose className="h-4 w-4" />
                    </button>
                  </div>
                  <Separator />
                  <ScrollArea className="flex-1 px-2 py-2">
                    <div className="space-y-1">
                      {categories.map((cat) => (
                        <CategoryCard
                          key={cat.id}
                          category={cat}
                          isSelected={selectedCategoryId === cat.id}
                          activeCounts={activeCounts}
                          onClick={() => onSelectCategory(cat.id)}
                          lang={lang}
                        />
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Collapsed sidebar toggle */}
              {!sidebarOpen && (
                <div className="shrink-0 border-r border-border/60 flex flex-col items-center py-2 px-1 gap-1">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(true)}
                    className="h-8 w-8 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title={lang === 'fr' ? 'Afficher les bibliothèques' : 'Show libraries'}
                  >
                    <PanelLeftOpen className="h-4 w-4" />
                  </button>
                  {selectedCategoryId && (
                    <span className="text-[9px] text-muted-foreground [writing-mode:vertical-lr] rotate-180 mt-1">
                      {categories.find((c) => c.id === selectedCategoryId)?.displayName || categories.find((c) => c.id === selectedCategoryId)?.name}
                    </span>
                  )}
                </div>
              )}

              {/* Right: sounds grid */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Category header */}
                {selectedCategoryId && (
                  <div className="flex-shrink-0 px-4 py-2 border-b border-border/40 bg-muted/10">
                    <p className="text-sm font-semibold">
                      {categories.find((c) => c.id === selectedCategoryId)?.displayName || categories.find((c) => c.id === selectedCategoryId)?.name || 'Categorie'}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {filteredSounds.length} {lang === 'fr' ? `son${filteredSounds.length > 1 ? 's' : ''}` : `sound${filteredSounds.length > 1 ? 's' : ''}`}
                      {loopSounds.length > 0 && (
                        <span className="ml-2">
                          <Repeat className="inline h-2.5 w-2.5 mr-0.5" />
                          {loopSounds.length} loops
                        </span>
                      )}
                      {oneshotSounds.length > 0 && (
                        <span className="ml-2">
                          <Zap className="inline h-2.5 w-2.5 mr-0.5" />
                          {oneshotSounds.length} oneshots
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {/* Sounds grid */}
                <ScrollArea className="flex-1">
                  <div className="p-3">
                    {/* Loop sounds section */}
                    {loopSounds.length > 0 && (
                      <div className="mb-4">
                        {oneshotSounds.length > 0 && (
                          <div className="flex items-center gap-2 mb-2">
                            <Repeat className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-semibold text-primary">
                              Ambiances / Loops
                            </span>
                            <Separator className="flex-1" />
                          </div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {loopSounds.map((sound) => (
                            <SoundCard
                              key={sound.id}
                              sound={sound}
                              isActive={activeSoundIds.has(sound.id)}
                              volume={soundVolumes[sound.id]}
                              defaultVolume={defaultSoundVolume}
                              onToggle={onToggleSound}
                              onSetVolume={onSetSoundVolume}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Oneshot sounds section */}
                    {oneshotSounds.length > 0 && (
                      <div>
                        {loopSounds.length > 0 && (
                          <div className="flex items-center gap-2 mb-2">
                            <Zap className="h-3.5 w-3.5 text-amber-500" />
                            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                              Effets sonores / One-shots
                            </span>
                            <Separator className="flex-1" />
                          </div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {oneshotSounds.map((sound) => (
                            <SoundCard
                              key={sound.id}
                              sound={sound}
                              isActive={activeSoundIds.has(sound.id)}
                              volume={soundVolumes[sound.id]}
                              defaultVolume={defaultSoundVolume}
                              onToggle={onToggleSound}
                              onSetVolume={onSetSoundVolume}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {filteredSounds.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <AudioLines className="h-10 w-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">{lang === 'fr' ? 'Aucun son trouvé' : 'No sounds found'}</p>
                        {searchTerm && (
                          <p className="text-xs mt-1">
                            {lang === 'fr' ? 'Essayez un autre terme de recherche' : 'Try a different search term'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default SoundboardLibrary;
